// API service for Form11 Assessment Summary data management
// Handles all API calls related to Form11 data operations

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class Form11Service {
  // Helper method to handle API responses
  static async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Helper method to get auth headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Insert new Form11 data
  static async insertForm11Data(formData) {
    try {
    
      const response = await fetch(`${API_BASE_URL}/form11`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const result = await this.handleResponse(response);
      
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error inserting Form11 data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get Form11 data by AppId
  static async getForm11Data(appId) {
    try {
      if (!appId) {
        throw new Error('AppId is required');
      }

    
      const response = await fetch(`${API_BASE_URL}/form11/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error fetching Form11 data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update Form11 data
  static async updateForm11Data(appId, formData) {
    try {
      if (!appId) {
        throw new Error('AppId is required');
      }

    
      const response = await fetch(`${API_BASE_URL}/form11/${appId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const result = await this.handleResponse(response);
      
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error updating Form11 data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete Form11 data (soft delete)
  static async deleteForm11Data(appId) {
    try {
      if (!appId) {
        throw new Error('AppId is required');
      }

     
      const response = await fetch(`${API_BASE_URL}/form11/${appId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error deleting Form11 data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get all Form11 records with pagination and search
  static async getAllForm11Data({ 
    page = 1, 
    limit = 10, 
    sortBy = 'InsertDate', 
    sortOrder = 'DESC', 
    search = '' 
  } = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
        ...(search && { search })
      });

     
      const response = await fetch(`${API_BASE_URL}/form11?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);
      
      return {
        success: true,
        data: result.data,
        pagination: result.pagination,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error fetching Form11 data list:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Helper method to prepare form data for API submission
  static prepareFormDataForSubmission(formData, additionalData = {}) {
    // Clean and format the data according to API expectations

    console.log('assessment dates',formData.assessmentDates)
    const cleanedData = {
      // Required fields
      appId: formData.appId || additionalData.appId,
      
      // Optional fields with proper formatting
      labtypeid: formData.labtypeid ? parseInt(formData.labtypeid) : null,
      cabName: formData.cabName || null,
      cabId: formData.cabId ? parseInt(formData.cabId) : null,
      field: formData.field || null,
      dis_Existing: '', // ASPX sends empty string, discipline data stored separately
      dis_Scope: formData.dis_Scope || null,
      facility: formData.facility || null,
      premise: formData.premise || null,
      assessmentType: formData.assessmentType || null,
      currentStatus:formData.currentStatus || null,
      extnValidity:formData.extnValidity || null,
      processWitness: formData.otherQualityMeasures || additionalData.otherQualityMeasures || null,
      // Date fields - ensure proper formatting
      dateReceipt: formData.dateReceipt ? new Date(formData.dateReceipt).toISOString() : null,
      
      // Text fields
      assessmentDates: formData.assessmentDates || null,
      assessmentTeam: formData.assessmentTeam || null,
      internalAudit: formData.internalAudit || null,
      managementReview: formData.managementReview || null,
      pt_ILC: formData.pt_ILC || null,
      processWitnessMajor: formData.processWitnessMajor || null,
      ncStatus: formData.ncStatus || null,
      secratoryObsv: formData.secratoryObsv || null,
      assessmentSummary: formData.assessmentSummary || null,
      assessorRecommendation: formData.assessorRecommendation || null,
      ptIlcRemark: formData.ptIlcRemark || null,
      acComment: formData.acComment || null,
      decisionProcess: formData.decisionProcess || null,
      
      // New Quality Assurance Assessment fields
      trainedAuditor: additionalData.trainedAuditor || null,
      independenceOfActivity: additionalData.independenceOfActivity || null,
      ncsClosed: additionalData.ncsClosed || null,
      effective: additionalData.effective || null,
      frequencyOfAudit: additionalData.frequencyOfAudit || null,
      allPointsCovered: additionalData.allPointsCovered || null,
      conductedWithinYear: additionalData.conductedWithinYear || null,
      ptAvailability: additionalData.ptAvailability || null,
      ptNotAvailableReason: additionalData.ptNotAvailableReason || null,
      ptCoverageOfScope: additionalData.ptCoverageOfScope || null,
      ptOutlier: additionalData.ptOutlier || null,
      ptOutlierRemarks: additionalData.ptOutlierRemarks || null,
      ilcCoverageOfScope: additionalData.ilcCoverageOfScope || null,
      ilcOutlier: additionalData.ilcOutlier || null,
      ilcOutlierRemarks: additionalData.ilcOutlierRemarks || null,
      
      is_Active: formData.is_Active !== undefined ? formData.is_Active : true,
      ...additionalData
    };

    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === undefined) {
        delete cleanedData[key];
      }
    });

    return cleanedData;
  }

  // Validate form data before submission
  static validateFormData(formData) {
    const errors = [];

    // Required field validation
    if (!formData.appId) {
      errors.push('Application ID is required');
    }

    // Type validation
    if (formData.appId && isNaN(parseInt(formData.appId))) {
      errors.push('Application ID must be a valid number');
    }

    if (formData.labtypeid && isNaN(parseInt(formData.labtypeid))) {
      errors.push('Lab Type ID must be a valid number');
    }

    if (formData.cabId && isNaN(parseInt(formData.cabId))) {
      errors.push('CAB ID must be a valid number');
    }

    // Date validation
    if (formData.dateReceipt && isNaN(new Date(formData.dateReceipt).getTime())) {
      errors.push('Date of Receipt must be a valid date');
    }


    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Get Form11 data from the current form state (for integration with Form11.js)
  static extractFormDataFromState({
    cabDetails,
    selectedDisciplines,
    assessorData,
    facilityTypes,
    assessmentReportData,
    formInputs = {}
  }) {
    return {
      appId: cabDetails?.appId || cabDetails?.ApplicationId,
      labtypeid: cabDetails?.labTypeId || null,
      cabName: cabDetails?.labName || cabDetails?.laboratoryName,
      cabId: cabDetails?.cabId || null,
      field: cabDetails?.labType || cabDetails?.field,
      dis_Existing: '', // ASPX sends empty string, discipline data stored separately
      dis_Scope: null, // To be filled based on scope extension data
      facility: this.formatFacilityTypes(facilityTypes),
      premise: cabDetails?.AddressLine1 || cabDetails?.AddressLine2,
      assessmentType: cabDetails?.applicationType,
      currentStatus: cabDetails?.currentStatus || null,
      dateReceipt: cabDetails?.submissionDate,
      extnValidity: null, // To be filled based on extension data
      assessmentDates: this.formatAssessmentDates(assessorData),
      assessmentTeam: this.formatAssessmentTeam(assessorData),
      
      // Quality Assurance Measures
      internalAudit: formInputs.internalAudit || null,
      managementReview: formInputs.managementReview || null,
      pt_ILC: this.formatPTILC(formInputs),
      processWitness: formInputs.otherQualityMeasures || null,
      major: null, // To be filled from NC data
      minor: null, // To be filled from NC data
      
      // NC Status
      ncStatus: this.formatNCStatus(formInputs),
      
      // Secretary Observations (Combined concerned team observations)
      secratoryObsv: this.formatSecratoryObservations(formInputs),
      
      // Assessment Data
      assessmentSummary: assessmentReportData?.assessmentSummary || null,
      assessorRecommendation: assessmentReportData?.recommendationOfAssessmentTeam || null,
      
      // PT/ILC Remark
      ptIlcRemark: this.formatPTILCRemark(formInputs),
      
      // AC Comments
      acComment: formInputs.acProcessComments || null,
      
      // Decision Process (if selected)
      decisionProcess: formInputs.decisionProcess || null,
      
      // Additional fields for complete schema
      suspensionReply: null,
      suspensionReplyFile: null,
      cabAcceptance: null,
      gatc_Recommend: null,
      generateAAFEE: null,
      forwardToJD: null,
      jDirectorAccepted: null,
      jDirectorDenyReason: null,
      jDirectorAcceptDate: null,
      jDirectorIP: null,
      forwardToID: null,
      
      // Active status
      is_Active: 1
    };
  }

  // Helper method to format PT/ILC data
  static formatPTILC(formInputs) {
    const ptData = formInputs.ptParticipation || 'Not specified';
    const ilcData = formInputs.ilcParticipation || 'Not specified';
    return `PT Participation: ${ptData} | ILC Participation: ${ilcData}`;
  }

  // Helper method to format NC Status
  static formatNCStatus(formInputs) {
    const totalNc = formInputs.totalNc || 0;
    const additionalInfo = formInputs.ncAdditionalInfo || '';
    return `Total Non-Conformities: ${totalNc}${additionalInfo ? ` | Additional Info: ${additionalInfo}` : ''}`;
  }

  // Helper method to format Secretary Observations
  static formatSecratoryObservations(formInputs) {
    const observations = [];
    
    if (formInputs.advisorConsultant) {
      observations.push(`Advisor/Consultant: ${formInputs.advisorConsultant}`);
    }
    
    if (formInputs.adequacyOrganization) {
      observations.push(`Organization Adequacy: ${formInputs.adequacyOrganization}`);
    }
    
    if (formInputs.otherInformation) {
      observations.push(`Other Information: ${formInputs.otherInformation}`);
    }
    
    if (formInputs.observationRecommendation) {
      observations.push(`Observations & Recommendations: ${formInputs.observationRecommendation}`);
    }
    
    return observations.length > 0 ? observations.join(' | ') : null;
  }

  // Helper method to format PT/ILC Remark
  static formatPTILCRemark(formInputs) {
    const remarks = [];
    
    if (formInputs.ptParticipation) {
      remarks.push(`PT: ${formInputs.ptParticipation}`);
    }
    
    if (formInputs.ilcParticipation) {
      remarks.push(`ILC: ${formInputs.ilcParticipation}`);
    }
    
    return remarks.length > 0 ? remarks.join(' | ') : null;
  }

  // Helper method to format facility types
  static formatFacilityTypes(facilityTypes) {
    if (!facilityTypes) return null;
    
    const activeTypes = [];
    if (facilityTypes.PermanentFacility) activeTypes.push('Permanent Facility');
    if (facilityTypes.SiteFacility) activeTypes.push('Site Facility');
    if (facilityTypes.MobileFacility) activeTypes.push('Mobile Facility');
    
    return activeTypes.join(', ') || null;
  }

  // Helper method to format assessment dates
  static formatAssessmentDates(assessorData) {
    if (!assessorData || !Array.isArray(assessorData) || assessorData.length === 0) {
      return null;
    }

    const dates = assessorData.map(assessor => {
      const fromDate = assessor.fromDate || assessor.startDate;
      const toDate = assessor.toDate || assessor.endDate;
      if (fromDate && toDate) {
        return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
      }
      return null;
    }).filter(date => date !== null);

    return dates.length > 0 ? dates.join('; ') : null;
  }

  // Helper method to format assessment team
  static formatAssessmentTeam(assessorData) {
    if (!assessorData || !Array.isArray(assessorData) || assessorData.length === 0) {
      return null;
    }

    const teamMembers = assessorData.map(assessor => {
      const name = assessor.assessorName;
      const capacity = assessor.capacity || assessor.assessorCapacity || 'Technical Assessor';
      const expertise = assessor.fieldOfExpertise || assessor.expertise;
      
      return `${name} (${capacity}${expertise ? ` - ${expertise}` : ''})`;
    });

    return teamMembers.join('; ') || null;
  }

  // Update JD Decision (Approve/Reject)
  static async updateJDDecision(decisionData) {
    try {
      console.log('🚀 Sending JD decision update:', decisionData);
      
      const response = await fetch(`${API_BASE_URL}/form11/jd-decision`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(decisionData)
      });

      const result = await this.handleResponse(response);
      
      console.log('✅ JD decision update successful:', result);
      
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } catch (error) {
      console.error('❌ Error updating JD decision:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Validate Form11 data - all fields are mandatory except acProcessComments
  static validateFormData(formData) {
    const errors = [];

    // List of required fields (all fields except acProcessComments)
    const requiredFields = [
      { field: 'internalAudit', label: 'Internal Audit' },
      { field: 'internalAuditType', label: 'Internal Audit Type' },
      { field: 'trainedAuditor', label: 'Trained Auditor' },
      { field: 'independenceOfActivity', label: 'Independence of Activity' },
      { field: 'ncsClosed', label: 'NCs Closed' },
      { field: 'effective', label: 'Effective' },
      { field: 'frequencyOfAudit', label: 'Frequency of Audit' },
      { field: 'managementReview', label: 'Management Review' },
      { field: 'managementReviewType', label: 'Management Review Type' },
      { field: 'allPointsCovered', label: 'All Points Covered' },
      { field: 'currentStatus', label: 'Current Status' },
      { field: 'extnValidity', label: 'Extension Validity' },
      { field: 'conductedWithinYear', label: 'Conducted Within Year' },
      { field: 'ptParticipation', label: 'PT Participation' },
      { field: 'ptAvailability', label: 'PT Availability' },
      { field: 'ptCoverageOfScope', label: 'PT Coverage of Scope' },
      { field: 'ptOutlier', label: 'PT Outlier' },
      { field: 'ilcParticipation', label: 'ILC Participation' },
      { field: 'ilcCoverageOfScope', label: 'ILC Coverage of Scope' },
      { field: 'ilcOutlier', label: 'ILC Outlier' },
      { field: 'otherQualityMeasures', label: 'Other Quality Measures' },
      { field: 'advisorConsultant', label: 'Advisor Consultant' },
      { field: 'adequacyOrganization', label: 'Adequacy Organization' },
      { field: 'otherInformation', label: 'Other Information' },
      { field: 'observationRecommendation', label: 'Observation Recommendation' },
      { field: 'facilityTypes', label: 'Facility Types' },
      { field: 'decisionProcess', label: 'Decision Process' }
    ];

    // Check each required field
    requiredFields.forEach(({ field, label }) => {
      const value = formData[field];
      
      // Check if field is empty, null, undefined, or just whitespace
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`${label} is required`);
      }
      
      // Special handling for object fields like facilityTypes
      if (field === 'facilityTypes' && typeof value === 'object') {
        const selectedTypes = Object.values(value || {}).filter(Boolean);
        if (selectedTypes.length === 0) {
          errors.push(`At least one ${label} must be selected`);
        }
      }
    });

    // Conditional validation for PT fields
    if (formData.ptParticipation === 'Yes' || formData.ptParticipation === 'yes') {
      if (!formData.ptParticipationType || formData.ptParticipationType.trim() === '') {
        errors.push('PT Participation Type is required when PT Participation is Yes');
      }
    }

    if (formData.ptAvailability === 'No' || formData.ptAvailability === 'no') {
      if (!formData.ptNotAvailableReason || formData.ptNotAvailableReason.trim() === '') {
        errors.push('PT Not Available Reason is required when PT is not available');
      }
    }

    if (formData.ptOutlier === 'Yes' || formData.ptOutlier === 'yes') {
      if (!formData.ptOutlierRemarks || formData.ptOutlierRemarks.trim() === '') {
        errors.push('PT Outlier Remarks are required when PT Outlier is Yes');
      }
    }

    if (formData.ilcOutlier === 'Yes' || formData.ilcOutlier === 'yes') {
      if (!formData.ilcOutlierRemarks || formData.ilcOutlierRemarks.trim() === '') {
        errors.push('ILC Outlier Remarks are required when ILC Outlier is Yes');
      }
    }

    // Note: acProcessComments is NOT validated as it's optional

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
}

export default Form11Service;